import { createSelector } from 'reselect'
import _ from 'lodash'
import {
    getCurrentMedia,
    getCurrentMediaRecordLocationKeywords,
} from 'redux/currentMedia/selectors'
import {
    Rectangle,
    Position,
    Vector,
} from 'helpers/math'
import {
    getProjection,
    computeViewData,
} from 'redux/canvas/selectors'
import { getCurrentMediaRecordPeriod } from '../currentMedia/selectors'
import { isResponsiveModeEnabled } from '../visualizer/selectors'
import { getCollaborativeIndexation } from '../configuration/selectors'

export const getCollaborativeIndexations = (state) => state.collaborativeIndexations

export const getCollaborativeIndexationsList = createSelector(
    getCollaborativeIndexations,
    collaborativeIndexation => collaborativeIndexation && collaborativeIndexation.list
)

export const areCollaborativeIndexationsDisplayed = createSelector(
    getCollaborativeIndexations,
    isResponsiveModeEnabled,
    (collaborativeIndexations, isResponsiveModeEnabled) => ! isResponsiveModeEnabled && collaborativeIndexations.areDisplayed === true
)

export const hasCollaborativeIndexations = createSelector(
    getCollaborativeIndexationsList,
    (state, media) => media,
    (collaborativeIndexationsList, media) => collaborativeIndexationsList.hasOwnProperty(media.uuid)
)

export const getCurrentMediaCollaborativeIndexationsList = createSelector(
    getCollaborativeIndexationsList,
    getCurrentMedia,
    (collaborativeIndexationsList, currentMedia) => {
        if(
            ! collaborativeIndexationsList
            || ! collaborativeIndexationsList.hasOwnProperty(currentMedia.uuid)
            || ! Array.isArray(collaborativeIndexationsList[currentMedia.uuid])
        )
        {
            return []
        }

        return collaborativeIndexationsList[currentMedia.uuid]
    }
)

export const countCurrentMediaCollaborativeIndexationList = createSelector(
    getCurrentMediaCollaborativeIndexationsList,
    collaborativeIndexations => collaborativeIndexations.length
)

export const getCollaborativeIndexationCampaign = createSelector(
    getCollaborativeIndexations,
    collaborativeIndexations => collaborativeIndexations && collaborativeIndexations.campaign,
)

export const getCollaborativeIndexationCampaignAnnotationLimit = createSelector(
    getCollaborativeIndexationCampaign,
    collaborativeIndexationCampaign => collaborativeIndexationCampaign && collaborativeIndexationCampaign.annotationLimit
)

export const isCurrentMediaCollaborativeAnnotationLimitReached = createSelector(
    countCurrentMediaCollaborativeIndexationList,
    getCollaborativeIndexationCampaignAnnotationLimit,
    (collaborativeIndexationsCount, collaborativeIndexationsLimit) => (
        collaborativeIndexationsLimit !== undefined
        && collaborativeIndexationsLimit !== -1
        && collaborativeIndexationsCount >= collaborativeIndexationsLimit
    )
)

export const getCollaborativeIndexationsZones = createSelector(
    getCurrentMediaCollaborativeIndexationsList,
    collaborativeIndexations =>
    {
        return collaborativeIndexations.map(zone => {
            const { width, height, x, y } = zone.position

            return {
                uuid: zone.uuid,
                rect: new Rectangle(width, height, new Position(x, y))
            }
        })
    }
)

export const hasCurrentMediaCollaborativeIndexation = createSelector(
    getCurrentMediaCollaborativeIndexationsList,
    (state, collaborativeIndexationId) => collaborativeIndexationId,
    (currentMediaCollaborativeIndexationsList, collaborativeIndexationId) => {
        const collaborativeIndexation =  _.find(currentMediaCollaborativeIndexationsList, { 'uuid': collaborativeIndexationId });

        return collaborativeIndexation !== undefined
    }
)

export const getSelectedZone = createSelector(
    getCollaborativeIndexations,
    collaborativeIndexations => collaborativeIndexations.selectedZone
)

export const getSelectedCollaborativeIndexationsFields = createSelector(
    getCurrentMediaCollaborativeIndexationsList,
    getSelectedZone,
    (indexations, selectedZone) =>
    {
        if(! Array.isArray(indexations))
        {
            return null
        }

        let zoneFields = null
        indexations.forEach((zone) => {
            if(zone.uuid === selectedZone)
            {
                zoneFields = zone.fields
            }
        })

        return zoneFields
    }
)

export const isCollaborativeIndexationSummaryOpened = createSelector(
    getCollaborativeIndexations,
    collaborativeIndexation => collaborativeIndexation.isSummaryOpened === true
)

export const isCollaborativeIndexationSelected = createSelector(
    getSelectedZone,
    (state, uuid) => uuid,
    (selectedZone, uuid) => selectedZone === uuid
)

export const isCollaborativeIndexationCreationOpened = createSelector(
    getCollaborativeIndexations,
    (collaborativeIndexations) => collaborativeIndexations.creation.isOpened === true
)

export const isCollaborativeIndexationCreationLocked = createSelector(
    getCollaborativeIndexations,
    collaborativeIndexations => collaborativeIndexations.creation.isLocked === true
)

export const getCollaborativeIndexationSelection = createSelector(
    getCollaborativeIndexations,
    (collaborativeIndexations) => collaborativeIndexations.creation.zone
)

export const hasCollaborativeIndexationSelection = createSelector(
    getCollaborativeIndexationSelection,
    collaborativeIndexationSelection => collaborativeIndexationSelection !== null
)

export const isCollaborativeIndexationSelectionDefined = createSelector(
    getCollaborativeIndexationSelection,
    selection => selection instanceof Rectangle
)

export const computeMousePositionRelativeToImage = createSelector(
    getProjection,
    computeViewData,
    (state, viewId, mousePosition) => mousePosition,
    (projection, viewData, mousePosition) => {

        const { frame } = projection
        const { rotation, offset } = viewData

        mousePosition = mousePosition.convertToVector()
            .add(new Vector(frame.width / 2, frame.height / 2).invert())
            .rotate(-rotation)
            .add(new Vector(frame.width / 2, frame.height / 2))
            .convertToPosition()

        return mousePosition.move(
            new Vector(frame.width / 2 - offset.x, frame.height / 2 - offset.y).invert()
        )
    }
)

export const getCollaborativeIndexationCampaignIndexationType = createSelector(
    getCollaborativeIndexationCampaign,
    collaborativeIndexationcampaign => collaborativeIndexationcampaign && collaborativeIndexationcampaign.indexationType,
)

export const hasCollaborativeIndexationCampaign = createSelector(
    getCollaborativeIndexationCampaign,
    collaborativeIndexationCampaign => !! collaborativeIndexationCampaign && collaborativeIndexationCampaign.hasOwnProperty('indexationType')
)

export const getCollaborativeIndexationCreation = createSelector(
    getCollaborativeIndexations,
    collaborativeIndexations => collaborativeIndexations.creation
)

export const getCollaborativeIndexationCreationFields = createSelector(
    getCollaborativeIndexationCreation,
    collaborativeIndexationCreation => collaborativeIndexationCreation.fields
)

export const getCollaborativeIndexationCreationField = createSelector(
    getCollaborativeIndexationCreationFields,
    (state, fieldName) => fieldName,
    (collaborativeIndexationCreationFields,  fieldName) => collaborativeIndexationCreationFields[fieldName]
)

export const getCollaborativeIndexationCreationCurrentStep = createSelector(
    getCollaborativeIndexationCreation,
    collaborativeIndexationCreation => collaborativeIndexationCreation.currentStep
)

export const isCollaborativeIndexationCreationSelectionToolActive = createSelector(
    getCollaborativeIndexationCreation,
    collaborativeIndexationCreation => collaborativeIndexationCreation.selectionToolActive === true
)

export const isCollaborativeIndexationCreationInvalidField = createSelector(
    getCollaborativeIndexationCreation,
    (state, field) => field,
    (collaborativeIndexationCreation, field) => {
        return _.indexOf(collaborativeIndexationCreation.invalidFields, field) !== -1
    }
)

export const isCollaborativeIndexationCreationCloseConfirmationNeeded = createSelector(
    getCollaborativeIndexationCreation,
    collaborativeIndexationCreation => collaborativeIndexationCreation.closeConfirmationNeeded === true
)

export const getCollaborativeIndexationCampaignDefaultEventDateMode = createSelector(
    getCollaborativeIndexationCampaign,
    collaborativeIndexationcampaign => {
        if(collaborativeIndexationcampaign.defaultEventDateMode)
        {
            return collaborativeIndexationcampaign.defaultEventDateMode
        }

        return null
    },
)

export const getCollaborativeIndexationCampaignDefaultEventPlaceMode = createSelector(
    getCollaborativeIndexationCampaign,
    collaborativeIndexationcampaign => {
        if(collaborativeIndexationcampaign.defaultEventPlaceMode)
        {
            return collaborativeIndexationcampaign.defaultEventPlaceMode
        }

        return null
    },
)

export const getCollaborativeIndexationCampaignDepartments = createSelector(
    getCollaborativeIndexationCampaign,
    collaborativeIndexationCampaign => (collaborativeIndexationCampaign && collaborativeIndexationCampaign.departments) || []
)

export const getCollaborativeIndexationCampaignCountries = createSelector(
    getCollaborativeIndexationCampaign,
    collaborativeIndexationCampaign => (collaborativeIndexationCampaign && collaborativeIndexationCampaign.countries) || []
)

const getLocationLabel = (locations, location) => {
    if(typeof location === "string")
    {
        const index = _.findIndex(locations, locationData => locationData.value === location)

        if(index !== -1)
        {
            return locations[index].label
        }
    }

    if(typeof location === "object" && location.hasOwnProperty("label"))
    {
        return location.label
    }

    return ''
}

export const getDepartmentLabel = createSelector(
    getCollaborativeIndexationCampaignDepartments,
    (state, department) => department,
    (departments, department) => getLocationLabel(departments, department)
)

export const getCountryLabel = createSelector(
    getCollaborativeIndexationCampaignCountries,
    (state, country) => country,
    (countries, country) => getLocationLabel(countries, country)
)

export const getCollaborativeIndexationCampaignDefaultDepartment = createSelector(
    getCollaborativeIndexationCampaign,
    collaborativeIndexationCampaign => collaborativeIndexationCampaign && collaborativeIndexationCampaign.defaultDepartment
)

export const getCollaborativeIndexationCampaignDefaultCountry = () => 'france'

export const getDefaultEventYear = createSelector(
    getCollaborativeIndexationCampaignDefaultEventDateMode,
    getCurrentMediaRecordPeriod,
    (defaultEventDateMode, period) => {
        if(defaultEventDateMode !== 'recordDate')
        {
            return null
        }

        if(period === null)
        {
            return null
        }

        const beginDate = new Date(period[0])
        const endDate = new Date(period[1])

        if(beginDate.getFullYear() !== endDate.getFullYear())
        {
            return null
        }

        return beginDate.getFullYear()
    }
)

export const getDefaultEventPlace = createSelector(
    getCollaborativeIndexationCampaignDefaultEventPlaceMode,
    getCurrentMediaRecordLocationKeywords,
    (defaultEventPlaceMode, locationKeywords) => {
        if(defaultEventPlaceMode !== 'locationKeyword')
        {
            return null
        }

        if(locationKeywords === null)
        {
            return null
        }

        if(locationKeywords.length !== 1)
        {
            return null
        }

        return locationKeywords[0]
    }
)

export const getMilitaryRecordRegistries = createSelector(
    getCollaborativeIndexationCampaign,
    campaign => campaign.militaryRecordRegistries || []
)

export const getDefaultMilitaryRecordRegistry = createSelector(
    getMilitaryRecordRegistries,
    militaryRecordRegistries => {
        if(militaryRecordRegistries.length === 1)
        {
            return militaryRecordRegistries[0]
        }

        return null
    }
)

export const getCollaborativeIndexationFormFields = createSelector(
    getCollaborativeIndexation,
    getCollaborativeIndexationCampaignIndexationType,
    (collaborativeIndexation, indexationType) => {
        if(! collaborativeIndexation.forms.hasOwnProperty(indexationType))
        {
            return null
        }

        const fields = collaborativeIndexation.forms[indexationType].fields
        let formattedFields = {}
        fields.forEach(value => {
            formattedFields[value] = null
        })

        return formattedFields
    }
)

export const getCollaborativeIndexationMinimumSelectionSize = createSelector(
    getCollaborativeIndexation,
    configuration => configuration.selection.minimumSize,
)
